import { useCallback, useEffect, useRef, useState } from 'react'

import { Stack, Typography, useTheme } from '@mui/material'
import { BoxIcon } from 'lucide-react'

import { Alert, Button, Modal } from '@repo/ui'
import { EncodeSessionBody, PACKAGE_HANDLE_URL_RE, familyForTagShopProduct } from 'components'
import type { EncodeSessionTarget } from 'components'
import { useEncodingDevice } from 'contexts'
import { useEncodeSession } from 'hooks'
import { api } from 'services'

interface BulkEncodeTagsModalProps {
  open: boolean
  /**
   * 'scan' — the operator scans each box's handle label on the reader to resolve it (top-bar
   * "Add tags"). 'queue' — a pre-built ordered list of targets from the table's bulk-select.
   */
  mode: 'scan' | 'queue'
  /** Ordered targets for 'queue' mode (ignored in 'scan' mode). */
  queue?: EncodeSessionTarget[]
  onClose: () => void
  refetch?: () => void
}

/**
 * VERA-790 — the BULK "Add tags" assembly-line, as a MODAL (was `EncodePackagePage`'s bulk mode).
 * Fill one box after another without leaving the Packages list: scan a box (or walk a pre-selected
 * queue), tap its tags, hit "Next box", repeat. Shares the exact session surface (`EncodeSessionBody`)
 * and machinery (`useEncodeSession`) with the single-package `EncodeTagsModal`, so the two flows are
 * identical tag-by-tag and the reader loop lives in one place.
 */
export const BulkEncodeTagsModal = (props: BulkEncodeTagsModalProps) => {
  const { open, mode, queue, onClose, refetch } = props
  const theme = useTheme()
  const { isConnected, readerName, helperReachable, subscribeScan } = useEncodingDevice()

  // Current box being filled (full target — carries family/product identity + capacity). Null
  // while awaiting the next box (scanning, or between queued packages).
  const [target, setTarget] = useState<EncodeSessionTarget | null>(null)
  // Lock is chosen BEFORE a box's session starts and fixed for its duration (default OFF).
  const [lock, setLock] = useState(false)
  // VRG-836 — write protection (rotate app key 0 + gate the chip's NDEF write/change rights
  // behind it). A SEPARATE opt-in from `lock`, defaulting OFF, so turning on today's lock can
  // never start rotating key 0. Reset with the session, like `lock`.
  const [writeProtect, setWriteProtect] = useState(false)
  const [queueIndex, setQueueIndex] = useState(0)
  const [boxesFilled, setBoxesFilled] = useState(0)
  // Scan mode: a scanned handle is being resolved (GET /packages/:id in flight) + any resolve error.
  const [resolving, setResolving] = useState(false)
  const [resolveError, setResolveError] = useState<string | null>(null)
  const resolvingRef = useRef(false)

  const session = useEncodeSession()
  const {
    provisioned,
    packageTagCount,
    runSession,
    stopSession,
    resetSession,
    seedPackageCount,
  } = session

  const inSession = Boolean(target)
  const targetCount = target?.targetCount
  const bigCount = packageTagCount != null ? packageTagCount : provisioned
  const isPackageFilled = targetCount != null && bigCount >= targetCount
  const queueExhausted = mode === 'queue' && !!queue && queueIndex >= queue.length

  // Start a box's session with its full run config (target carries family + product; lock is the
  // current UI toggle). `t` is passed explicitly so a just-resolved/just-advanced box starts the
  // INSTANT it's known, with no stale-state race.
  const runForTarget = useCallback(
    (t: EncodeSessionTarget) => {
      seedPackageCount(t.id)
      runSession({
        target: { kind: 'package', id: t.id },
        family: t.family,
        lock,
        writeProtect,
        productSku: t.productSku,
        productTitle: t.productTitle,
        productImage: t.productImage,
      })
    },
    [lock, runSession, seedPackageCount],
  )

  // Reset ALL modal state on open/close so each open starts a clean cycle (and closing tears down
  // the loop + reader lock). Cleanup also runs on unmount via the hook's own effect.
  useEffect(() => {
    if (open) return
    stopSession()
    resetSession()
    setTarget(null)
    setLock(false)
    setWriteProtect(false)
    setQueueIndex(0)
    setBoxesFilled(0)
    setResolving(false)
    setResolveError(null)
    resolvingRef.current = false
  }, [open, stopSession, resetSession])

  // QUEUE mode — load `queue[queueIndex]` the instant there's no current box, auto-starting its
  // session. Stops once the queue is exhausted (the placeholder then shows completion).
  useEffect(() => {
    if (!open || mode !== 'queue' || !queue || target) return
    if (queueIndex >= queue.length) return
    const t = queue[queueIndex]
    setTarget(t)
    runForTarget(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, queue, queueIndex, target])

  // SCAN mode — while awaiting a box (no current target), listen for the reader scanning a box's
  // handle label (the plain NDEF URL AddPackagesModal writes) and resolve it into a fill target,
  // then auto-start that box's session immediately.
  useEffect(() => {
    if (!open || mode !== 'scan' || target) return
    const unsub = subscribeScan((scannedUrl) => {
      if (resolvingRef.current) return
      const match = PACKAGE_HANDLE_URL_RE.exec(scannedUrl)
      if (!match) return
      const packageId = match[1]
      resolvingRef.current = true
      setResolving(true)
      setResolveError(null)
      ;(async () => {
        try {
          // `api.get` resolves even on 4xx (the interceptor never rejects for a known error
          // response) — check `status` explicitly rather than relying on a throw.
          const response = await api.get(`/packages/${packageId}`)
          const status = response?.status
          const pkgData = response?.data
          if (!pkgData?.id || (typeof status === 'number' && (status < 200 || status >= 300))) {
            setResolveError("That package label isn't recognized")
            return
          }
          const tagShopProduct = pkgData.tag_shop_product ?? null
          const resolved: EncodeSessionTarget = {
            kind: 'package',
            id: pkgData.id,
            name: pkgData.name,
            family: familyForTagShopProduct(tagShopProduct),
            productSku: tagShopProduct?.selected_variant?.sku ?? tagShopProduct?.sku ?? undefined,
            productTitle: tagShopProduct?.selected_variant?.title ?? tagShopProduct?.title ?? undefined,
            productImage:
              tagShopProduct?.selected_variant?.image ?? tagShopProduct?.images?.[0] ?? undefined,
            lock: false,
            targetCount: tagShopProduct?.variants?.find(
              (v: any) => v?.id === tagShopProduct?.selected_variant?.id,
            )?.quantity,
          }
          setTarget(resolved)
          runForTarget(resolved)
        } catch {
          setResolveError("That package label isn't recognized")
        } finally {
          resolvingRef.current = false
          setResolving(false)
        }
      })()
    })
    return () => {
      unsub()
      resolvingRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, target, subscribeScan])

  // Advance from the just-filled box to the next. Stops this box's loop, tallies it (only if at
  // least one tag was provisioned), clears the session, and — in queue mode — advances the index
  // (the loader effect picks up the next package); in scan mode, clearing the target re-arms the
  // scan effect for the next handle tap.
  const handleNextBox = useCallback(() => {
    stopSession()
    setBoxesFilled((c) => c + (provisioned > 0 ? 1 : 0))
    resetSession()
    setTarget(null)
    setResolveError(null)
    if (mode === 'queue') setQueueIndex((i) => i + 1)
  }, [stopSession, resetSession, provisioned, mode])

  const handleDone = useCallback(() => {
    refetch?.()
    onClose()
  }, [refetch, onClose])

  const placeholder = (
    <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
      <BoxIcon strokeWidth={1} size={32} color={theme.palette.text.disabled} />
      <Typography variant="body3" color="text.secondary">
        {mode === 'queue'
          ? queueExhausted
            ? `All ${queue?.length ?? 0} package${(queue?.length ?? 0) === 1 ? '' : 's'} filled.`
            : `Loading package ${queueIndex + 1} of ${queue?.length ?? 0}…`
          : resolving
            ? 'Resolving scanned box…'
            : 'Scan a box to begin.'}
      </Typography>
      {mode === 'scan' && resolveError && <Alert severity="error" description={resolveError} />}
    </Stack>
  )

  const header = (
    <Stack spacing={0.5}>
      {boxesFilled > 0 && (
        <Typography variant="body3" color="text.secondary">
          {boxesFilled} box{boxesFilled === 1 ? '' : 'es'} filled this session
        </Typography>
      )}
      {mode === 'queue' && queue && (
        <Typography variant="body3" color="text.secondary">
          Package {Math.min(queueIndex + 1, queue.length)} of {queue.length}
          {target ? ` — ${target.name}` : ''}
        </Typography>
      )}
    </Stack>
  )

  return (
    <Modal
      title="Add tags"
      open={open}
      onClose={handleDone}
      actions={
        <>
          {/* Next box — advance the assembly line without leaving. Only while a box is active. */}
          {inSession && (
            <Button variant="secondary" onClick={handleNextBox}>
              Next box
            </Button>
          )}
          <Button variant="primary" onClick={handleDone}>
            Done
          </Button>
        </>
      }
    >
      <EncodeSessionBody
        session={session}
        helperReachable={helperReachable}
        isConnected={isConnected}
        readerName={readerName}
        lock={lock}
        setLock={setLock}
        writeProtect={writeProtect}
        setWriteProtect={setWriteProtect}
        // VRG-252 — the current box's family, so the lock warning names the mechanism it triggers.
        family={target?.family ?? null}
        bigCount={bigCount}
        targetCount={targetCount}
        isPackageFilled={isPackageFilled}
        counterLabel="in this package"
        header={header}
        active={inSession}
        placeholder={placeholder}
      />
    </Modal>
  )
}
